import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { RootState } from './store';
import { RouteCardsPagination, RouteCardsQueryParams, RouteCardsResponse } from '../interfaces';
import { DEFAULT_ROUTE_CARDS_PAGE_SIZE, getRouteCards } from '../api/getRouteCards';

export interface RouteCardsState {
    items: RouteCardsResponse['items'] | null;
    pagination: RouteCardsPagination;
    loading: boolean;
    error: string | null;
    currentRequestId: string | null;
}

const initialState: RouteCardsState = {
    items: null,
    pagination: {
        page: 1,
        pageSize: DEFAULT_ROUTE_CARDS_PAGE_SIZE,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
    },
    loading: false,
    error: null,
    currentRequestId: null,
};

export const loadRouteCards = createAsyncThunk<RouteCardsResponse, RouteCardsQueryParams | undefined>(
    'routeCards/load',
    async (params, thunkApi) => {
        return await getRouteCards(
            params || { page: 1, pageSize: DEFAULT_ROUTE_CARDS_PAGE_SIZE },
            thunkApi.signal,
        );
    },
);

export const RouteCardsSlice = createSlice({
    name: 'routeCards',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(loadRouteCards.pending, (state, action) => {
                state.loading = true;
                state.error = null;
                state.currentRequestId = action.meta.requestId;
            })
            .addCase(loadRouteCards.fulfilled, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) {
                    return;
                }

                state.items = action.payload.items;
                state.pagination = action.payload.pagination;
                state.loading = false;
                state.currentRequestId = null;
            })
            .addCase(loadRouteCards.rejected, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) {
                    return;
                }

                state.loading = false;
                state.currentRequestId = null;
                if (!action.meta.aborted) {
                    state.error = action.error.message || 'Failed to load route cards.';
                    console.error(action.error);
                }
            });
    },
});

export const selectRouteCards = (state: RootState) => state.routeCards.items;
export const selectRouteCardsPagination = (state: RootState) => state.routeCards.pagination;
export const selectRouteCardsLoading = (state: RootState) => state.routeCards.loading;
export const selectRouteCardsError = (state: RootState) => state.routeCards.error;

export default RouteCardsSlice.reducer;
